import { TODAY } from '../../constants';
import type { Client, Phase } from '../../types/domain';
import { isoStr } from '../dates';
import { isCancelledPhase } from './cancellation';

export function getActivePhase(client: Client): Phase | null {
  const todayIso = isoStr(TODAY);
  const phases = client.phases || [];
  return (
    phases.find((ph) => {
      const started = !ph.dateDebut || ph.dateDebut <= todayIso;
      const notEnded = !ph.dateFin || ph.dateFin >= todayIso;
      return started && notEnded;
    }) ||
    phases[phases.length - 1] ||
    null
  );
}

// Phases actives d'un client à la date du jour (peut y en avoir plusieurs, ex. Lahaye BEMOD/LAH/LM).
export function activePhases(client: Client): Phase[] {
  const todayIso = isoStr(TODAY);
  return (client.phases || []).filter(
    (ph) => (!ph.dateDebut || ph.dateDebut <= todayIso) && (!ph.dateFin || ph.dateFin >= todayIso),
  );
}

// Phase ouverte : sans date de fin, ou qui finit après aujourd'hui (arbitrage du 05/10/2026).
// Clôturer met la fin à aujourd'hui et une phase annulée finit avant son début : ni l'une ni
// l'autre n'est ouverte. Même règle pour le badge « Clôturée » et pour la planification.
export function isPhaseOpen(phase: Phase, todayIso: string = isoStr(TODAY)): boolean {
  return !phase.dateFin || phase.dateFin > todayIso;
}

// Phases proposables pour le tiroir "général" d'un client : toutes (feuille de temps / remap Jira,
// on peut rattraper une saisie sur une phase déjà terminée) ou seulement les ouvertes (Timeline,
// on ne planifie jamais sur une phase clôturée ou annulée).
export function eligiblePhasesForGeneral(client: Client, onlyOpen: boolean): Phase[] {
  const all = client.phases || [];
  return onlyOpen ? all.filter((ph) => isPhaseOpen(ph)) : all;
}

// Phase à présélectionner dans le menu "général par phase" : la seule active s'il n'y en a qu'une,
// sinon aucune présélection (l'utilisateur doit trancher explicitement entre plusieurs actives).
export function defaultActivePhaseId(client: Client): string {
  const actives = activePhases(client);
  return actives.length === 1 ? actives[0].id : '';
}

// Phase résumée sur la carte d'un client (liste Projets, arbitrage du 06/10/2026) : une phase
// ouverte en priorité (déjà commencée, sinon la prochaine à commencer) ; sans phase ouverte, la
// plus récemment terminée (clôturée avant annulée à date égale), pour garder ses vrais chiffres
// visibles sous un badge de statut plutôt que de masquer la carte. null seulement si le client n'a
// vraiment aucune phase.
export function cardPhase(client: Client): Phase | null {
  const todayIso = isoStr(TODAY);
  const phases = client.phases || [];
  const open = phases.filter((ph) => isPhaseOpen(ph, todayIso));
  const started = open.find((ph) => !ph.dateDebut || ph.dateDebut <= todayIso);
  if (started) return started;
  const upcoming = [...open].sort((a, b) => (a.dateDebut || '').localeCompare(b.dateDebut || ''));
  if (upcoming[0]) return upcoming[0];
  const closed = phases.filter((ph) => !isPhaseOpen(ph, todayIso));
  if (!closed.length) return null;
  return [...closed].sort((a, b) => {
    const parDate = (b.dateFin || '').localeCompare(a.dateFin || '');
    return parDate !== 0 ? parDate : Number(isCancelledPhase(a)) - Number(isCancelledPhase(b));
  })[0];
}
